import { examMetadataModule } from '../examMetadata/module'
import { userModule } from '../user/module'
import { notebookModule } from './module'
import type { NotebookItemJSON } from './types'
import { runCloudFunction } from '../parseUtils'
import axios from 'axios'
import type { TTutorState } from '../tutor/state'
import { userExamMetadataModule } from '../userExamMetadata/module'

/**
 * Fetch all items saved to notebook
 */
type FetchNotebookItems = (params: { examMetadataId: string }) => NotebookItemJSON[]
const fetchNotebookItems = async () => {
    // if there are any notebook items in store, we're just going to assume that's all of them
    if (notebookModule.state.notebookItems.length > 0) {
        return notebookModule.state.notebookItems
    }

    // otherwise, go fetch all of the notebook items to populate the store
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])
    const examMetadata = examMetadataModule.getters.getCurrentExamMetadata()

    if (!examMetadata) {
        return []
    }

    // fetch all notebook items for the user and put in store
    notebookModule.state.notebookItems = await runCloudFunction<FetchNotebookItems>(
        'fetchNotebookItems',
        {
            examMetadataId: examMetadata.objectId,
        }
    )

    return notebookModule.state.notebookItems
}

/**
 * Fetch Tutor Sessions that have been saved as notebook items
 */
const fetchNotebookTutorSessions = async () => {
    const examMetadata = examMetadataModule.getters.getCurrentExamMetadata()
    const user = userModule.state.user
    const sessionToken = userModule.getters.getSessionToken()

    if (!user || !examMetadata || !sessionToken) {
        throw new Error('Missing required data.')
    }

    await fetchNotebookItems()
    const tutorNotebookItems = notebookModule.getters.getItemsGroupedByType().get('tutor-session') || []

    if (!tutorNotebookItems.length) {
        return {}
    }

    const pocketbrainUrlEnvVariable = import.meta.env.VUE_APP_POCKETBRAIN_URL
    const tutorUrl = `${pocketbrainUrlEnvVariable}/fetch-tutor-session`

    const tutorSessions = (await Promise.all(tutorNotebookItems.map(async i => {
        if (i.metadata.type === 'tutor-session' && i.metadata.questionSerial) {
            const axiosResponse = await axios.post(
                tutorUrl,
                {
                    examMetadataId: examMetadata.objectId,
                    questionSerial: i.metadata.questionSerial,
                },
                {
                    headers: {
                        'session-token': sessionToken,
                        'Content-Type': 'application/json',
                    },
                }
            )

            const chat = axiosResponse?.data?.chat
            if (!chat) {
                return
            }

            return {
                chat,
                questionSerial: i.metadata.questionSerial,
            }
        }
    }))).filter(s => !!s)

    return tutorSessions.reduce<{ [questionSerial: string]: TTutorState['chat'] }>((acc, ts) => {
        acc[ts.questionSerial] = ts.chat
        return acc
    }, {})
}

export default {
    fetchNotebookItems,
    fetchNotebookTutorSessions,
}
